import React, {
  FocusEvent,
  InputHTMLAttributes,
  RefObject,
  KeyboardEvent,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import clsx from 'clsx';
import MaskedTextField from 'core/components/MaskedTextField';
import {
  TextFieldProps,
} from '@mui/material/TextField';
import isNil from 'lodash/isNil';
import {
  formatTwoDigitDate,
  parseFlexibleDate,
} from 'utils/date';
import dayjs from 'utils/dayjs';
import type {
  DateInputValue,
} from 'core/components/formValue';
import {
  isEmptyString,
} from '@populateui/utils';

const dateMask = [{
  mask: 'MM{/}DD{/}YYYY',
  blocks: {
    MM: {
      mask: '00',
      definitions: {
        0: /[0-9]/,
      },
      placeholderChar: 'm',
    },
    DD: {
      mask: '00',
      definitions: {
        0: /[0-9]/,
      },
      placeholderChar: 'd',
    },
    YYYY: {
      mask: '0000',
      definitions: {
        0: /[0-9]/,
      },
      placeholderChar: 'y',
    },
  },
  lazy: true,
  overwrite: true,
  eager: false,
}];

const monthYearMask = [{
  mask: 'MM{/}YYYY',
  blocks: {
    MM: {
      mask: '00',
      definitions: {
        0: /[0-9]/,
      },
      placeholderChar: 'm',
    },
    YYYY: {
      mask: '0000',
      definitions: {
        0: /[0-9]/,
      },
      placeholderChar: 'y',
    },
  },
  lazy: true,
  overwrite: true,
  eager: false,
}];

type DateValue = DateInputValue;
type MaskType = 'date' | 'monthYear';

function normalizeValue(val: DateValue): DateValue {
  let normalizedValue: DateValue;

  if (val instanceof Date || typeof val === 'string') {
    normalizedValue = val;
  } else {
    normalizedValue = undefined;
  }

  return normalizedValue;
}

function formatValue(val: DateValue, maskType: MaskType): string | undefined {
  let formattedValue: string | undefined;

  if (val instanceof Date) {
    formattedValue = maskType === 'monthYear'
      ? dayjs(val).format('MM/YYYY')
      : formatTwoDigitDate(val);
  } else if (typeof val === 'string') {
    formattedValue = val;
  } else if (isNil(val)) {
    formattedValue = undefined;
  } else {
    formattedValue = String(val);
  }

  return formattedValue;
}

type Props = {
  id?: string;
  value?: DateValue;
  onChange: (value: DateValue) => void;
  isValueAllowed?: (value: DateValue) => boolean;
  invalidValueMessage?: string;
  onInvalidValue?: () => void;
  onInputErrorChange?: (message: string | null) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  className?: string;
  inputRef?: RefObject<HTMLInputElement | null>;
  nullIfEmpty?: boolean;
  disabled?: boolean;
  placeholder?: string;
  slotProps?: TextFieldProps['slotProps'];
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  maskType?: MaskType;
};

export const dateInputAntiAutofillAttributes = {
  autoComplete: 'off',
  role: 'presentation',
  'data-lpignore': 'true',
  'data-1p-ignore': '',
  'data-form-type': 'other',
} as const;

export function applyDateInputAntiAutofill(input: HTMLInputElement) {
  input.setAttribute('autocomplete', dateInputAntiAutofillAttributes.autoComplete);
  input.setAttribute('role', dateInputAntiAutofillAttributes.role);
  input.setAttribute('data-lpignore', dateInputAntiAutofillAttributes['data-lpignore']);
  input.setAttribute('data-1p-ignore', dateInputAntiAutofillAttributes['data-1p-ignore']);
  input.setAttribute('data-form-type', dateInputAntiAutofillAttributes['data-form-type']);
}

export default function DateTextField({
  value: _value,
  className,
  onChange,
  isValueAllowed,
  invalidValueMessage,
  onInvalidValue,
  onInputErrorChange,
  onKeyDown,
  nullIfEmpty = false,
  disabled = false,
  placeholder = 'mm/dd/yyyy',
  inputProps,
  maskType = 'date',
  ...props
}: Props) {
  const committedValue = useMemo(() => normalizeValue(_value), [_value]);
  const committedFormattedValue = formatValue(committedValue, maskType);
  const [value, setValue] = useState<DateValue>(committedFormattedValue);
  const isAllowedValue = (nextValue: DateValue) => isValueAllowed?.(nextValue) ?? true;
  const inputErrorMessage = useMemo(() => {
    const nextFormattedValue = formatValue(value, maskType);

    if (nextFormattedValue === committedFormattedValue) {
      return null;
    }

    const parsedValue = dayjs(
      nextFormattedValue,
      maskType === 'monthYear' ? 'MM/YYYY' : 'MM/DD/YYYY',
      true,
    );

    if (!parsedValue.isValid()) {
      return 'Invalid date';
    }

    if (!isAllowedValue(value)) {
      return invalidValueMessage ?? 'Invalid date';
    }

    return null;
  }, [committedFormattedValue, invalidValueMessage, isAllowedValue, maskType, value]);

  useEffect(() => {
    setValue((previousValue) => {
      const currentValue = formatValue(previousValue, maskType);
      const syncedValue = committedFormattedValue !== currentValue
        ? committedFormattedValue
        : previousValue;

      return syncedValue;
    });
  }, [committedFormattedValue, maskType]);

  useEffect(() => {
    onInputErrorChange?.(inputErrorMessage);
  }, [inputErrorMessage, onInputErrorChange]);

  // Antifill attrs are applied via ref because MaskedTextField does not forward htmlInput.
  const setInputRef = useCallback((node: HTMLInputElement | null) => {
    if (!isNil(props.inputRef)) {
      // eslint-disable-next-line no-param-reassign -- forward ref to caller
      props.inputRef.current = node;
    }

    if (!isNil(node)) {
      applyDateInputAntiAutofill(node);
    }
  }, [props.inputRef]);

  return (
      <MaskedTextField
        {...props}
        mask={maskType === 'monthYear' ? monthYearMask : dateMask}
        variant="standard"
        placeholder={placeholder}
        value={formatValue(value, maskType)}
        inputRef={setInputRef}
      onAccept={setValue}
      slotProps={{
        ...props.slotProps,
        htmlInput: {
          ...(typeof props.slotProps?.htmlInput === 'object' ? props.slotProps.htmlInput : undefined),
          ...inputProps,
        },
        input: {
          ...props.slotProps?.input,
          disableUnderline: true,
        },
      }}
      disabled={disabled}
      onComplete={(nextValue: string) => {
        if (dayjs(nextValue).isValid() && isAllowedValue(nextValue)) {
          onChange(nextValue);
        } else if (!isAllowedValue(nextValue)) {
          onInvalidValue?.();
        }
      }}
      onBlur={(e: FocusEvent<HTMLInputElement>) => {
        const rawValue = value?.toString() ?? '';
          if (isEmptyString(rawValue)) {
            const clearedValue = nullIfEmpty ? undefined : '';
            setValue(clearedValue);
            onChange(clearedValue);
          } else {
            const parsed = maskType === 'monthYear'
              ? dayjs(rawValue, 'MM/YYYY', true)
              : parseFlexibleDate(rawValue);
            if (!isNil(parsed) && parsed.isValid()) {
              const normalized = maskType === 'monthYear'
                ? parsed.format('MM/YYYY')
                : formatTwoDigitDate(parsed.toDate());
              if (isAllowedValue(normalized)) {
                setValue(normalized);
                onChange(normalized);
            } else {
              onInvalidValue?.();
            }
          } else if (!isAllowedValue(value)) {
            onInvalidValue?.();
          }
        }
        props.onBlur?.(e);
      }}
      onKeyDown={onKeyDown}
      className={clsx('populate-input populate-date-input text-xs', className)}
    />
  );
}
